import React, { useState, useEffect } from 'react';
import { payhereAPI } from '../lib/api';
import { toast } from 'sonner';
import { Loader2, ArrowRight, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-LK', { style: 'currency', currency: 'LKR' }).format(amount || 0);

export const MigrateLegacyPayHereDialog = ({ open, onOpenChange, onDone }) => {
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState({});

  const load = async () => {
    setLoading(true);
    setSelected({});
    try {
      const res = await payhereAPI.getMigrationCandidates();
      setCandidates(res.data.candidates || []);
    } catch (e) {
      toast.error('Failed to load legacy sales');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const selectedTotal = candidates
    .filter((c) => selected[c.id])
    .reduce((sum, c) => sum + (c.amount || 0), 0);

  const toggleAll = (checked) => {
    const next = {};
    if (checked) candidates.forEach((c) => (next[c.id] = true));
    setSelected(next);
  };

  const migrate = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one transaction');
      return;
    }
    setMigrating(true);
    try {
      const res = await payhereAPI.migrate({ transaction_ids: selectedIds });
      toast.success(res.data.message || 'Migration complete');
      onOpenChange(false);
      if (onDone) onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Migration failed');
    } finally {
      setMigrating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="migrate-legacy-dialog">
        <DialogHeader>
          <DialogTitle>Migrate Legacy PayHere Sales</DialogTitle>
          <DialogDescription>
            These sales were posted directly to the Main Bank Account. Select the ones paid via PayHere to move
            them into the PayHere Clearing account and mark them as pending settlement.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
          </div>
        ) : candidates.length === 0 ? (
          <div className="text-center py-10 text-slate-400" data-testid="no-candidates">
            No legacy sales found in the Main Bank Account to migrate.
          </div>
        ) : (
          <>
            <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              This adjusts existing entries in place: the debit moves from Main Bank to PayHere Clearing. Only select
              transactions that were actually paid through PayHere.
            </div>
            <div className="max-h-80 overflow-y-auto mt-3 border rounded-md">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 px-2">
                      <Checkbox
                        checked={selectedIds.length === candidates.length && candidates.length > 0}
                        onCheckedChange={toggleAll}
                        data-testid="migrate-select-all"
                      />
                    </th>
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Order</th>
                    <th className="py-2 pr-4">Customer</th>
                    <th className="py-2 pr-4">Method</th>
                    <th className="py-2 pr-4 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c) => (
                    <tr key={c.id} className="border-b hover:bg-slate-50" data-testid={`candidate-row-${c.id}`}>
                      <td className="py-2 px-2">
                        <Checkbox
                          checked={!!selected[c.id]}
                          onCheckedChange={(v) => setSelected((prev) => ({ ...prev, [c.id]: v }))}
                          data-testid={`migrate-select-${c.id}`}
                        />
                      </td>
                      <td className="py-2 pr-4">{c.entry_date}</td>
                      <td className="py-2 pr-4 font-medium text-slate-800">{c.order_number || `#${c.woo_order_id || '-'}`}</td>
                      <td className="py-2 pr-4">{c.customer_name || '-'}</td>
                      <td className="py-2 pr-4 text-slate-500">{c.payment_method_title || '—'}</td>
                      <td className="py-2 pr-4 text-right font-semibold">{formatCurrency(c.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-3 text-sm">
              <span className="text-slate-500">{selectedIds.length} selected</span>
              <span className="font-semibold">{formatCurrency(selectedTotal)}</span>
            </div>
            {selectedIds.length > 0 && (
              <div className="text-xs text-slate-500 rounded-md border border-slate-200 p-3 mt-1 space-y-1">
                <p className="flex items-center gap-1"><ArrowRight className="h-3 w-3" /> Debit: PayHere Clearing {formatCurrency(selectedTotal)}</p>
                <p className="flex items-center gap-1"><ArrowRight className="h-3 w-3" /> (removed from Main Bank Account)</p>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={migrating}>Cancel</Button>
          <Button
            onClick={migrate}
            disabled={migrating || selectedIds.length === 0}
            data-testid="confirm-migrate-btn"
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {migrating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Migrate {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
